/**
 * The API's wire shapes, as this app consumes them.
 *
 * HONESTY RULE: every type here mirrors a handler that actually exists in
 * `apps/api-worker/src/routes/*.ts`. Nothing is invented. Where the POS needs
 * data the API does not expose (single-order detail, payment list, device mode
 * lookup by activation code…) there is NO type here — the screen renders an
 * explicit "cần endpoint X" placeholder instead.
 *
 * Two casing conventions coexist on purpose because the API has two:
 *   - mutation responses are camelCase (`totalMinor`),
 *   - read endpoints that `SELECT *` return raw rows in snake_case
 *     (`total_minor`).
 * Mapping one onto the other would hide the difference and produce silent
 * `undefined`s, so the split is kept visible in the types.
 */

export type Millis = number;
export type Minor = number;

// ---------------------------------------------------------------------------
// auth  (routes/auth.ts)
// ---------------------------------------------------------------------------

export interface LoginRequest {
  tenantCode: string;
  username: string;
  password: string;
  app: 'ADMIN' | 'POS' | 'STAFF';
  branchId?: string | null;
  stationId?: string | null;
  deviceId?: string | null;
  turnstileToken?: string | null;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: Millis;
  mustChangePassword: boolean;
  mfaRequired: boolean;
}

export interface MeResponse {
  user: {
    id: string;
    username: string;
    fullName: string;
    locale: string;
    employeeId: string | null;
    employeeCode: string | null;
    isPlatformAdmin: boolean;
  };
  tenant: { id: string; code: string; name: string };
  session: {
    id: string;
    app: string;
    activeBranchId: string | null;
    activeStationId: string | null;
    /**
     * The ONLY device datum the API returns to a POS session. Notably NOT the
     * device_mode — see `src/device/mode.ts` for how the app copes.
     */
    deviceId: string | null;
  };
  roles: Array<{
    code: string;
    branchId: string | null;
    stationId: string | null;
    dataScope: string;
    nameVi: string;
  }>;
  permissions: string[];
  permissionsByBranch: Record<string, string[]>;
  branches: Array<{ id: string; code: string; name: string; timezone: string; currency: string }>;
}

// ---------------------------------------------------------------------------
// organisation  (routes/organization.ts)
// ---------------------------------------------------------------------------

export type TableStatusWire =
  | 'EMPTY'
  | 'RESERVED'
  | 'OCCUPIED'
  | 'ORDERING'
  | 'WAITING_CONFIRMATION'
  | 'WAITING_FOOD'
  | 'PARTIALLY_SERVED'
  | 'FULLY_SERVED'
  | 'HELP_REQUESTED'
  | 'PAYMENT_REQUESTED'
  | 'PAYMENT_PROCESSING'
  | 'NEEDS_CLEANING'
  | 'SETTING_UP'
  | 'LOCKED'
  | 'OUT_OF_SERVICE';

export interface FloorMapTable {
  id: string;
  branchId: string;
  areaId: string;
  code: string;
  displayName: string;
  seats: number;
  minSeats: number | null;
  maxSeats: number | null;
  shape: 'RECT' | 'ROUND' | 'SQUARE' | 'CUSTOM';
  /** Geometry is NESTED on the wire, not flat posX/posY. */
  pos: { x: number; y: number };
  size: { width: number; height: number };
  rotation: number;
  status: TableStatusWire;
  assignedUserId: string | null;
  isActive: boolean;
  qrEnabled: boolean;
  rowVersion: number;
  session: null | {
    id: string;
    code: string;
    status: string;
    guestCount: number;
    openedAt: Millis | null;
    totalMinor: Minor;
    paidMinor: Minor;
    unservedItemCount: number;
  };
}

export interface FloorMapResponse {
  branchId: string;
  tables: FloorMapTable[];
}

export interface Area {
  id: string;
  branchId: string;
  code: string;
  name: string;
  floorLabel: string | null;
  areaType: string;
  serviceChargeBp: number | null;
  priceTier: string;
  sortOrder: number;
  isActive: boolean;
  rowVersion: number;
}

export interface AreasResponse {
  areas: Area[];
}

export interface Station {
  id: string;
  branchId: string;
  code: string;
  name: string;
  stationType: 'KITCHEN' | 'BAR' | 'DESSERT' | 'COLD' | 'GRILL' | 'FRY' | 'STEAM' | 'PACKING' | 'OTHER';
  sortOrder: number;
  defaultPrepSeconds: number;
  slaWarningRatioBp: number;
  maxConcurrentItems: number;
  overloadThreshold: number;
  autoAccept: boolean;
  printerId: string | null;
  isActive: boolean;
  rowVersion: number;
}

export interface StationsResponse {
  stations: Station[];
}

/** `devices.device_mode` — the authoritative list, mirrored from the API. */
export type DeviceModeWire =
  | 'POS_CASHIER'
  | 'SERVICE_TERMINAL'
  | 'KITCHEN_DISPLAY'
  | 'BAR_DISPLAY'
  | 'WAREHOUSE_TERMINAL'
  | 'CUSTOMER_ORDER'
  | 'QUEUE_DISPLAY'
  | 'MANAGER_TERMINAL'
  | 'STAFF_MOBILE';

export interface Device {
  id: string;
  branchId: string;
  deviceName: string;
  deviceMode: DeviceModeWire;
  stationId: string | null;
  terminalId: string | null;
  printerId: string | null;
  platform: string | null;
  activationStatus: 'PENDING' | 'ACTIVE' | 'REVOKED';
  activationExpiresAt: Millis | null;
  activatedAt: Millis | null;
  activatedBy: string | null;
  lastSeenAt: Millis | null;
  revokedAt: Millis | null;
  revokedReason: string | null;
  rowVersion: number;
  createdAt: Millis;
}

export interface DevicesResponse {
  devices: Device[];
}

export interface DeviceActivateResponse {
  ok: true;
  deviceId: string;
  activationStatus: 'ACTIVE';
}

// ---------------------------------------------------------------------------
// menu  (routes/menu.ts)
// ---------------------------------------------------------------------------

export interface MenuCategory {
  id: string;
  branchId: string | null;
  parentId: string | null;
  code: string;
  nameVi: string;
  descriptionVi: string | null;
  icon: string | null;
  imageFileId: string | null;
  sortOrder: number;
  isActive: boolean;
  visibleToCustomer: boolean;
  visibleToPos: boolean;
  availableFrom: string | null;
  availableTo: string | null;
  daysOfWeekMask: number;
  sourceVersion: number;
  rowVersion: number;
}

export interface CategoriesResponse {
  categories: MenuCategory[];
}

export interface Product {
  id: string;
  categoryId: string | null;
  code: string;
  nameVi: string;
  shortDescriptionVi: string | null;
  descriptionVi: string | null;
  productType: 'FOOD' | 'DRINK' | 'COMBO' | 'SERVICE' | 'OTHER';
  unitLabel: string;
  basePriceMinor: Minor;
  /** Present only when the actor holds `product.cost.view`. */
  baseCostMinor?: Minor;
  currency: string;
  taxRateBp: number;
  isTaxable: boolean;
  imageFileId: string | null;
  videoUrl: string | null;
  isVegetarian: boolean;
  isVegan: boolean;
  isAlcoholic: boolean;
  spiceLevel: number;
  calories: number | null;
  prepSeconds: number;
  defaultStationId: string | null;
  trackInventory: boolean;
  sellByWeight: boolean;
  allowDiscount: boolean;
  allowCustomerOrder: boolean;
  requiresAgeCheck: boolean;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'OUT_OF_STOCK' | 'DISCONTINUED';
  publishedAt: Millis | null;
  sortOrder: number;
  tags: string[];
  sourceVersion: number;
  rowVersion: number;
  createdAt: Millis;
  updatedAt: Millis;
}

export interface ProductListResponse {
  products: Product[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

export interface ProductVariant {
  id: string;
  productId: string;
  code: string;
  nameVi: string;
  variantType: string;
  priceDeltaMinor: Minor;
  prepSecondsDelta: number;
  isDefault: boolean;
  sortOrder: number;
  isActive: boolean;
  rowVersion: number;
}

export interface Modifier {
  id: string;
  modifierGroupId: string;
  code: string;
  nameVi: string;
  priceDeltaMinor: Minor;
  isDefault: boolean;
  sortOrder: number;
  maxQuantity: number;
  ingredientId: string | null;
  isActive: boolean;
  rowVersion: number;
}

export interface ModifierGroup {
  id: string;
  code: string;
  nameVi: string;
  selectionType: 'SINGLE' | 'MULTIPLE';
  minSelect: number;
  maxSelect: number;
  isRequired: boolean;
  sortOrder: number;
  /** ICE / SUGAR / SIZE / TOPPING… — this is what makes the bar view bar-shaped. */
  groupKind: 'TOPPING' | 'ICE' | 'SUGAR' | 'SPICE' | 'SIZE' | 'SIDE' | 'COOKING' | 'OTHER';
  isActive: boolean;
  sourceVersion: number;
  rowVersion: number;
  link: null | {
    isRequiredOverride: boolean | null;
    minSelectOverride: number | null;
    maxSelectOverride: number | null;
    sortOrder: number;
  };
  modifiers: Modifier[];
}

export interface ProductAllergen {
  id: string;
  code: string;
  severity: string;
  noteVi: string | null;
  confirmed: boolean;
  confirmedAt: Millis | null;
}

export interface ProductDetailResponse {
  product: Product;
  variants: ProductVariant[];
  modifierGroups: ModifierGroup[];
  allergens: ProductAllergen[];
  stationRoutes: Array<{
    id: string;
    branchId: string | null;
    categoryId: string | null;
    variantId: string | null;
    stationId: string;
    priority: number;
    prepSecondsOverride: number | null;
    isActive: boolean;
  }>;
  branchPrices: Array<{
    id: string;
    branchId: string | null;
    productId: string | null;
    variantId: string | null;
    priceMinor: Minor;
    validFrom: Millis | null;
    validTo: Millis | null;
    priority: number;
    isActive: boolean;
  }>;
}

export interface ProductAvailabilityResponse {
  ok: true;
  productId: string;
  branchId: string;
  businessDate: string | null;
  isAvailable: boolean;
  availabilityId: string;
}

// ---------------------------------------------------------------------------
// orders  (routes/orders.ts)
// ---------------------------------------------------------------------------

export type OrderStatus =
  | 'DRAFT'
  | 'PENDING_CONFIRMATION'
  | 'CONFIRMED'
  | 'IN_PRODUCTION'
  | 'PARTIALLY_SERVED'
  | 'SERVED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'VOIDED';

export type OrderItemStatus =
  | 'DRAFT'
  | 'PENDING'
  | 'CONFIRMED'
  | 'SENT_TO_STATION'
  | 'IN_PRODUCTION'
  | 'READY'
  | 'SERVED'
  | 'CANCELLED'
  | 'RETURNED'
  | 'VOIDED';

export interface OrderPricing {
  lines: Array<{
    ref: string;
    lineSubtotalMinor: Minor;
    modifierTotalMinor: Minor;
    itemDiscountMinor: Minor;
    orderDiscountMinor: Minor;
    discountMinor: Minor;
    serviceChargeMinor: Minor;
    taxMinor: Minor;
    lineTotalMinor: Minor;
  }>;
  subtotalMinor: Minor;
  itemDiscountMinor: Minor;
  orderDiscountMinor: Minor;
  serviceChargeMinor: Minor;
  taxMinor: Minor;
  roundingMinor: Minor;
  totalMinor: Minor;
  currency: string;
  exponent: number;
}

/** Raw `SELECT *` row. snake_case on purpose — see the file header. */
export interface OrderRow {
  id: string;
  tenant_id: string;
  branch_id: string;
  dining_session_id: string | null;
  table_id: string | null;
  order_no: string;
  business_date: string;
  order_type: string;
  channel: string;
  status: OrderStatus;
  payment_status: 'UNPAID' | 'PARTIALLY_PAID' | 'PAID' | 'REFUNDED' | 'PARTIALLY_REFUNDED' | 'VOID';
  guest_count: number;
  subtotal_minor: Minor;
  item_discount_minor: Minor;
  order_discount_minor: Minor;
  service_charge_minor: Minor;
  tax_minor: Minor;
  rounding_minor: Minor;
  total_minor: Minor;
  paid_minor: Minor;
  refunded_minor: Minor;
  currency: string;
  currency_exponent: number;
  note: string | null;
  kitchen_note: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  created_at: Millis;
  updated_at: Millis;
  row_version: number;
}

export interface OrderItemRow {
  id: string;
  order_id: string;
  tenant_id: string;
  branch_id: string;
  line_no: number;
  product_id: string;
  variant_id: string | null;
  product_name_snapshot: string;
  quantity: number;
  quantity_milli: number;
  unit_price_minor: Minor;
  modifier_total_minor: Minor;
  line_subtotal_minor: Minor;
  discount_minor: Minor;
  tax_minor: Minor;
  line_total_minor: Minor;
  station_id: string | null;
  status: OrderItemStatus;
  is_gift: 0 | 1;
  tax_rate_bp: number;
  is_taxable: 0 | 1;
  allow_discount: 0 | 1;
}

export interface DiningSessionRow {
  id: string;
  tenant_id: string;
  branch_id: string;
  area_id: string | null;
  table_id: string | null;
  session_code: string;
  session_type: string;
  guest_count: number;
  opened_at: Millis;
  closed_at: Millis | null;
  status: string;
  locale: string;
  locale_locked_by_guest: 0 | 1;
  subtotal_minor: Minor;
  discount_minor: Minor;
  service_charge_minor: Minor;
  tax_minor: Minor;
  total_minor: Minor;
  paid_minor: Minor;
  currency: string;
  max_devices: number;
  require_staff_confirm: 0 | 1;
  note: string | null;
  row_version: number;
  created_at: Millis;
  updated_at: Millis;
}

export interface SessionDetailResponse {
  session: DiningSessionRow;
  orders: Array<OrderRow & { items: OrderItemRow[] }>;
  serviceRequests: Array<{
    id: string;
    request_code: string;
    status: string;
    requested_at: Millis;
    free_text_vi: string | null;
    priority: number;
  }>;
  balance: {
    totalMinor: Minor;
    paidMinor: Minor;
    refundedMinor: Minor;
    outstandingMinor: Minor;
  };
}

export interface OrderListResponse {
  items: Array<OrderRow & { outstandingMinor: Minor }>;
  page: number;
  limit: number;
  total: number;
}

export interface ModifierInput {
  modifierId: string;
  modifierGroupId?: string | null;
  quantity?: number;
}

export interface LineInput {
  productId: string;
  variantId?: string | null;
  quantity?: number;
  noteVi?: string | null;
  spiceLevel?: number | null;
  courseNo?: number;
  seatNo?: number | null;
  priority?: number;
  clientUuid?: string | null;
  modifiers?: ModifierInput[];
}

export interface CreateOrderBody {
  branchId: string;
  diningSessionId?: string | null;
  tableId?: string | null;
  orderType?: 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY' | 'PRE_ORDER' | 'EVENT';
  channel?: 'POS' | 'CUSTOMER_QR' | 'STAFF_APP' | 'PHONE' | 'ONLINE' | 'THIRD_PARTY';
  priceTier?: 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY' | 'VIP' | 'EVENT';
  guestCount?: number;
  customerName?: string | null;
  customerPhone?: string | null;
  note?: string | null;
  kitchenNote?: string | null;
  clientUuid?: string | null;
  items: LineInput[];
}

export interface CreateOrderResponse {
  id: string;
  orderNo: string;
  businessDate: string;
  status: OrderStatus;
  diningSessionId: string | null;
  totals: OrderPricing;
}

export interface ConfirmOrderResponse {
  id: string;
  status: OrderStatus;
  sentItemCount: number;
}

export interface AddItemsResponse {
  id: string;
  status: OrderStatus;
  addedItemIds: string[];
  totals: OrderPricing;
}

export interface OpenSessionResponse {
  id: string;
  branchId: string;
  tableId: string | null;
  status: 'OPEN';
  openedAt: Millis;
}

export interface CloseSessionResponse {
  id: string;
  status: string;
  tableId: string | null;
  closedAt: Millis;
}

export interface DiscountResponse {
  id: string;
  amountMinor: Minor;
  effectiveBp: number;
  maxBp: number;
  totals: OrderPricing;
}

export interface GiftItemResponse {
  id: string;
  isGift: true;
  totals: OrderPricing;
}

export interface CancelItemResponse {
  id: string;
  status: OrderItemStatus;
  cancelStage: string;
  totals: OrderPricing;
}

export type TransferType = 'MOVE_TABLE' | 'MERGE_TABLE' | 'SPLIT_TABLE' | 'MOVE_ITEM' | 'SPLIT_BILL' | 'MERGE_BILL';

export interface TransferBody {
  transferType: TransferType;
  branchId: string;
  fromSessionId?: string | null;
  toSessionId?: string | null;
  toTableId?: string | null;
  fromOrderId?: string | null;
  toOrderId?: string | null;
  orderIds?: string[];
  itemIds?: string[];
  guestCount?: number | null;
}

export interface TransferResponse {
  ok: true;
  transferType: TransferType;
  fromSessionId: string | null;
  toSessionId: string | null;
  payload: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// payments  (routes/payments.ts)
// ---------------------------------------------------------------------------

export type PaymentMethod =
  | 'CASH'
  | 'BANK_TRANSFER'
  | 'QR_BANK'
  | 'CARD'
  | 'EWALLET'
  | 'VOUCHER'
  | 'GIFT_CARD'
  | 'LOYALTY_POINT'
  | 'CREDIT'
  | 'OTHER';

export interface CreatePaymentBody {
  branchId: string;
  orderId?: string | null;
  diningSessionId?: string | null;
  method: PaymentMethod;
  provider?: string | null;
  amountMinor: Minor;
  tenderedMinor?: Minor;
  tipMinor?: Minor;
  feeMinor?: Minor;
  referenceNo?: string | null;
  externalTxnId?: string | null;
  cashRegisterSessionId?: string | null;
  terminalId?: string | null;
  clientUuid?: string | null;
}

export interface CreatePaymentResponse {
  id: string;
  paymentNo: string;
  method: PaymentMethod;
  amountMinor: Minor;
  changeMinor: Minor;
  tipMinor: Minor;
  outstandingAfterMinor: Minor;
  allocations: Array<{ orderId: string; appliedMinor: Minor }>;
}

export interface InvoiceCreatedResponse {
  id: string;
  invoiceNo: string;
  invoiceType: string;
  orderId: string;
  subtotalMinor: Minor;
  taxMinor: Minor;
  totalMinor: Minor;
  currency: string;
  status: 'ISSUED';
}

export interface InvoiceExistingResponse {
  id: string;
  invoiceNo: string;
  status: string;
  alreadyIssued: true;
}

export type InvoiceResponse = InvoiceCreatedResponse | InvoiceExistingResponse;

/** The 200 path omits every money field — branch on this, never on status. */
export function isExistingInvoice(r: InvoiceResponse): r is InvoiceExistingResponse {
  return 'alreadyIssued' in r;
}

export interface OpenCashSessionResponse {
  id: string;
  businessDate: string;
  openingFloatMinor: Minor;
  status: 'OPEN';
}

export interface CloseCashSessionResponse {
  id: string;
  status: 'CLOSED';
  expectedCashMinor: Minor;
  countedCashMinor: Minor;
  /** SIGNED: counted − expected. Negative means the drawer is short. */
  varianceMinor: Minor;
  thresholdMinor: Minor;
  varianceBreached: boolean;
  totals: {
    cardMinor: Minor;
    transferMinor: Minor;
    ewalletMinor: Minor;
    otherMinor: Minor;
    salesMinor: Minor;
    refundMinor: Minor;
  };
}

export interface CashSessionDetailResponse {
  session: {
    id: string;
    branch_id: string;
    terminal_id: string | null;
    cashier_user_id: string;
    business_date: string;
    opened_at: Millis;
    closed_at: Millis | null;
    opening_float_minor: Minor;
    expected_cash_minor: Minor;
    counted_cash_minor: Minor;
    variance_minor: Minor;
    total_sales_minor: Minor;
    refund_total_minor: Minor;
    currency: string;
    status: 'OPEN' | 'COUNTING' | 'CLOSED' | 'RECONCILED' | 'DISPUTED';
    closing_note: string | null;
    row_version: number;
  };
  expectedCashMinor: Minor;
  movements: Array<{
    id: string;
    movement_type: string;
    amount_minor: Minor;
    reference_type: string | null;
    reference_id: string | null;
    reason: string | null;
    actor_user_id: string | null;
    created_at: Millis;
  }>;
  paymentsByMethod: Array<{ method: string; total: Minor; count: number }>;
  refunds: { totalMinor: Minor; count: number };
}

// ---------------------------------------------------------------------------
// production  (routes/production.ts) — the KDS / bar board
// ---------------------------------------------------------------------------

export type ProductionStatusWire =
  | 'WAITING'
  | 'OFFERED'
  | 'ACCEPTED'
  | 'PREPARING'
  | 'PAUSED'
  | 'WAITING_INGREDIENT'
  | 'COMPLETED'
  | 'WAITING_PICKUP'
  | 'PICKED_UP'
  | 'DELIVERED'
  | 'RETURNED'
  | 'REMAKE_REQUIRED'
  | 'REMAKING'
  | 'CANCELLED';

/**
 * `modifiers_snapshot_json` is typed `unknown[]` by the API because it is a
 * blob. The seed writes `{ code, name }`; this is the tolerant reader.
 */
export interface KdsModifierSnapshot {
  code?: string;
  name?: string;
  quantity?: number;
}

export interface KdsTicketItem {
  id: string;
  orderItemId: string;
  productId: string;
  variantId: string | null;
  name: string;
  quantity: number;
  modifiers: unknown[];
  note: string | null;
  allergenNote: string | null;
  spiceLevel: number | null;
  seatNo: number | null;
  courseNo: number | null;
  status: ProductionStatusWire;
  assignedUserId: string | null;
  acceptedAt: Millis | null;
  startedAt: Millis | null;
  pausedAt: Millis | null;
  completedAt: Millis | null;
  elapsedSeconds: number;
  slaDueAt: Millis;
  slaState: 'ON_TIME' | 'WARNING' | 'LATE';
  slaRemainingSeconds: number;
  remakeCount: number;
  returnCount: number;
  isRemake: boolean;
  rowVersion: number;
}

export interface KdsTicket {
  id: string;
  ticketNo: string;
  orderId: string;
  tableId: string | null;
  diningSessionId: string | null;
  businessDate: string;
  roundNo: number;
  ticketType: string;
  priority: number;
  status: ProductionStatusWire;
  note: string | null;
  allergenFlags: unknown[];
  itemCount: number;
  completedItemCount: number;
  sentAt: Millis | null;
  acceptedAt: Millis | null;
  acceptedByUserId: string | null;
  startedAt: Millis | null;
  elapsedSeconds: number;
  slaDueAt: Millis;
  slaState: 'ON_TIME' | 'WARNING' | 'LATE';
  slaRemainingSeconds: number;
  slaElapsedRatioBp: number;
  isLate: boolean;
  rowVersion: number;
  items: KdsTicketItem[];
}

export interface KdsBoardResponse {
  station: {
    id: string;
    code: string;
    name: string;
    type: string;
    domain: 'kitchen' | 'bar';
    branchId: string;
    autoAccept: boolean;
    slaWarningRatioBp: number;
  };
  /** Server clock. NEVER use the device clock for SLA — see @sonauto/utilities. */
  serverTime: Millis;
  counts: { total: number; late: number; warning: number };
  groups: Array<{ status: ProductionStatusWire; tickets: KdsTicket[] }>;
  tickets: KdsTicket[];
}

export interface StationLoadResponse {
  stationId: string;
  stationName: string;
  branchId: string;
  domain: 'kitchen' | 'bar';
  capturedAt: Millis;
  waitingCount: number;
  inProgressCount: number;
  waitingPickupCount: number;
  lateCount: number;
  oldestWaitingSeconds: number;
  avgPrepSeconds: number | null;
  activeStaffCount: number;
  loadRatioBp: number;
  overloadThreshold: number;
  isOverloaded: boolean;
  activeStatuses: ProductionStatusWire[];
}

export interface ProductionActionResponse {
  ok: true;
  itemId?: string;
  ticketId?: string;
  status: ProductionStatusWire;
}

// ---------------------------------------------------------------------------
// customer QR surface  (routes/customer.ts) — the multilingual one
// ---------------------------------------------------------------------------

export interface CustomerCurrency {
  /** ISO-4217 from the BRANCH. Never derived from the guest's language. */
  code: string;
  exponent: number;
}

export interface CustomerLocaleOption {
  locale: string;
  /** Endonym: `Tiếng Việt`, `中文`, `Русский`, `English`. */
  nameNative: string;
  nameVi: string;
  direction: string;
  isDefault: boolean;
}

export interface CustomerSessionResponse {
  session: {
    code: string;
    type: string;
    status: string;
    guestCount: number;
    openedAt: Millis;
    expiresAt: Millis;
    requiresStaffConfirmation: boolean;
    maxItemsPerOrder: number;
  };
  restaurant: { name: string; branchName: string };
  table: { displayName: string | null };
  device: { status: 'PENDING_APPROVAL' | 'APPROVED' | 'BLOCKED' };
  locale: string;
  localeSource: string;
  localeLockedByGuest: boolean;
  locales: CustomerLocaleOption[];
  currency: CustomerCurrency;
  cart: {
    orderCount: number;
    pendingConfirmationCount: number;
    itemCount: number;
    totalMinor: Minor;
    paidMinor: Minor;
  };
}

export interface CustomerSetLocaleResponse {
  locale: string;
  localeLockedByGuest: true;
  currency: CustomerCurrency;
  locales: CustomerLocaleOption[];
}

export interface CustomerMenuAllergen {
  code: string;
  severity: string;
  confirmed: boolean;
}

export interface CustomerMenuModifier {
  id: string;
  code: string;
  name: string;
  priceDeltaMinor: Minor;
  isDefault: boolean;
  maxQuantity: number;
  translated: boolean;
}

export interface CustomerMenuModifierGroup {
  id: string;
  code: string;
  name: string;
  selectionType: string;
  minSelect: number;
  maxSelect: number;
  isRequired: boolean;
  sortOrder: number;
  translated: boolean;
  modifiers: CustomerMenuModifier[];
}

export interface CustomerMenuVariant {
  id: string;
  code: string;
  name: string;
  priceMinor: Minor;
  priceDeltaMinor: Minor;
  isDefault: boolean;
  translated: boolean;
}

export interface CustomerMenuProduct {
  id: string;
  code: string;
  categoryId: string | null;
  name: string;
  shortDescription: string | null;
  description: string | null;
  productType: string;
  unitLabel: string;
  priceMinor: Minor;
  currency: string;
  imageFileId: string | null;
  spiceLevel: number;
  calories: number | null;
  isVegetarian: boolean;
  isVegan: boolean;
  isAlcoholic: boolean;
  requiresAgeCheck: boolean;
  allowCustomerOrder: boolean;
  tags: string[];
  isAvailable: boolean;
  outOfStockReason: string | null;
  remainingQty: number | null;
  translated: boolean;
  translationStatus: string | null;
  variants: CustomerMenuVariant[];
  modifierGroups: CustomerMenuModifierGroup[];
  allergens: CustomerMenuAllergen[];
}

export interface CustomerMenuCategory {
  id: string;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  translated: boolean;
  products: CustomerMenuProduct[];
}

export interface CustomerMenuResponse {
  locale: string;
  currency: CustomerCurrency;
  menu: {
    locale: string;
    sourceLocale: string;
    localeEnabled: boolean;
    fallbackBehaviour: string;
    /** Bare code inside `menu`, unlike the object at the top level. */
    currency: string;
    businessDate: string;
    categories: CustomerMenuCategory[];
    uncategorised: CustomerMenuProduct[];
    hiddenCount: number;
  };
}

export interface CustomerUiStringsResponse {
  locale: string;
  namespace: string;
  version: number;
  direction: string;
  /** Blank values are dropped server-side, so a missing key is simply absent. */
  entries: Record<string, string>;
}

export interface CustomerOrderItemInput {
  productId: string;
  variantId?: string | null;
  quantity: number;
  modifiers?: Array<{ modifierId: string; quantity?: number }>;
  note?: string | null;
  spiceLevel?: number | null;
}

export interface CustomerSubmitOrderResponse {
  orderNo: string;
  status: 'PENDING_CONFIRMATION' | 'CONFIRMED';
  requiresStaffConfirmation: boolean;
  itemCount: number;
  subtotalMinor: Minor;
  totalMinor: Minor;
  currency: CustomerCurrency;
  estimatedReadyAt: Millis;
  submittedAt: Millis;
  locale: string;
}

export type CustomerOrderStatusCode = 'ORDER_RECEIVED' | 'ORDER_PREPARING' | 'ORDER_COMPLETED' | 'ITEM_UNAVAILABLE';

export interface CustomerOrderItem {
  lineNo: number;
  name: string;
  nameIsFallback: boolean;
  quantity: number;
  status: string;
  statusCode: CustomerOrderStatusCode;
  statusMessage: string | null;
  lineTotalMinor: Minor;
  note: string | null;
  noteLocale: string | null;
  servedAt: Millis | null;
}

export interface CustomerOrder {
  orderNo: string;
  status: string;
  statusCode: CustomerOrderStatusCode;
  statusMessage: string | null;
  submittedAt: Millis;
  confirmedAt: Millis | null;
  subtotalMinor: Minor;
  totalMinor: Minor;
  note: string | null;
  estimatedReadyAt: Millis | null;
  items: CustomerOrderItem[];
}

export interface CustomerOrdersResponse {
  locale: string;
  currency: CustomerCurrency;
  orders: CustomerOrder[];
}

/** The business codes the API accepts. Never send free text as a code. */
export const CUSTOMER_REQUEST_CODES = [
  'CALL_STAFF',
  'REQUEST_WATER',
  'REQUEST_UTENSILS',
  'REQUEST_CLEAN_TABLE',
  'REQUEST_PAYMENT',
  'ASK_ORDER_STATUS',
  'REPORT_MISSING_ITEM',
  'REPORT_WRONG_ITEM',
  'REQUEST_SUPPORT',
] as const;

export type CustomerRequestCode = (typeof CUSTOMER_REQUEST_CODES)[number];

export interface CustomerServiceRequestResponse {
  requestCode: CustomerRequestCode;
  status: string;
  requestedAt: Millis;
  message: string | null;
  duplicate: boolean;
}

export interface CustomerRequestsResponse {
  locale: string;
  requests: Array<{
    requestCode: string;
    label: string | null;
    status: string;
    requestedAt: Millis;
    resolvedAt: Millis | null;
    freeText: string | null;
    freeTextLocale: string | null;
  }>;
}

export interface CustomerFeedbackResponse {
  ok: true;
  rating: number;
  submittedAt: Millis;
  locale: string;
}

// ---------------------------------------------------------------------------
// QR issuing + device self-activation  (routes/qr.ts)
// ---------------------------------------------------------------------------

/** `slip` on the qr-token response — mirrors `QrSlipPayload` in @sonauto/qr. */
export interface QrSlipWire {
  restaurantName: string;
  branchName: string;
  tableDisplayName: string;
  sessionCode: string;
  /** The URL to encode into the QR image. */
  qrUrl: string;
  issuedAt: Millis;
  expiresAt: Millis;
  validMinutes: number;
  guestCount: number | null;
  instructionsVi: string[];
  warningVi: string;
}

/**
 * POST /v1/dining-sessions/:id/qr-token. `token` appears in THIS response and
 * nowhere else — the server stores only its hash. Print immediately.
 */
export interface QrTokenIssueResponse {
  token: string;
  url: string;
  shortCode: string;
  expiresAt: Millis;
  tokenId: string;
  slip: QrSlipWire;
}

export interface QrTokenRevokeResponse {
  ok: true;
  sessionId: string;
  revokedCount: number;
}

/** POST /v1/devices/claim — UNAUTHENTICATED; the 6-char code IS the credential. */
export interface DeviceClaimResponse {
  deviceId: string;
  deviceMode: DeviceModeWire;
  branchId: string;
  stationId: string | null;
  terminalId: string | null;
  branchName: string;
}

/** GET /v1/devices/me — the session's own device row, NOT behind device.view. */
export interface DeviceMeResponse {
  device: {
    id: string;
    branchId: string;
    deviceName: string;
    deviceMode: DeviceModeWire;
    stationId: string | null;
    terminalId: string | null;
    printerId: string | null;
    platform: string | null;
    activationStatus: string;
    activatedAt: Millis | null;
    lastSeenAt: Millis | null;
    assignedPermissions: string[];
  };
}

// ---------------------------------------------------------------------------
// Table operations + pickup queue  (routes/tables-ops.ts)
// ---------------------------------------------------------------------------

/** A replayed offline tap answers `alreadyDone: true` rather than 409. */
export interface TableOpResponse {
  ok: true;
  tableId: string;
  status: TableStatusWire | string;
  taskId?: string | null;
  taskClosed?: boolean;
  alreadyDone?: boolean;
}

export interface PickupQueueOrder {
  orderId: string;
  /** What the counter calls out: session code for QR takeaway, else order no. */
  shortCode: string;
  orderNo: string;
  status: OrderStatus;
  orderType: string;
  customerName: string | null;
  totalMinor: Minor;
  paidMinor: Minor;
  paymentStatus: string;
  createdAt: Millis;
  itemCount: number;
  readyItemCount: number;
  servedItemCount: number;
  /** Every live item READY or SERVED, and at least one item exists. */
  ready: boolean;
}

export interface PickupQueueResponse {
  branchId: string;
  businessDate: string;
  computedAt: Millis;
  orders: PickupQueueOrder[];
}

// ---------------------------------------------------------------------------
// Approvals  (routes/approvals.ts)
// ---------------------------------------------------------------------------

export const APPROVAL_TYPES = [
  'PAYROLL_ADJUSTMENT',
  'SALARY_ADVANCE',
  'LEAVE_REQUEST',
  'SHIFT_SWAP',
  'STOCK_ADJUSTMENT',
  'PURCHASE_ORDER',
  'STOCK_COUNT',
  'KPI_APPEAL',
  'WASTAGE',
] as const;

export type ApprovalType = (typeof APPROVAL_TYPES)[number];

export interface ApprovalRequester {
  userId: string | null;
  employeeId: string | null;
  name: string | null;
}

export interface ApprovalItem {
  id: string;
  type: ApprovalType;
  titleVi: string;
  summaryVi: string;
  status: string;
  branchId: string | null;
  amountMinor: Minor | null;
  resourceType: string;
  resourceId: string;
  requiredPermission: string;
  requestedAt: Millis;
  requestedBy: ApprovalRequester | null;
}

export interface ApprovalsResponse {
  approvals: ApprovalItem[];
}

export interface ApprovalCountResponse {
  total: number;
  byType: Partial<Record<ApprovalType, number>>;
}

export interface ApprovalDecisionResponse {
  ok: true;
  type: ApprovalType;
  id: string;
  status: string;
}

// ---------------------------------------------------------------------------
// Realtime  (routes/realtime.ts + durable/branch-hub.ts)
// ---------------------------------------------------------------------------

export interface RealtimeTokenResponse {
  token: string;
  branchId: string;
  topics: string[];
  expiresAt: Millis;
  ttlSeconds: number;
}

/** One broadcast event as relayed by the BranchHub. `seq` is the resume cursor. */
export interface HubEventWire {
  seq: number;
  type: string;
  topic: string;
  at: Millis;
  payload: Record<string, unknown>;
  correlationId: string | null;
}

// ---------------------------------------------------------------------------
// Inventory  (routes/inventory.ts) — the slice the WAREHOUSE_TERMINAL uses
// ---------------------------------------------------------------------------

/** Quantities are INTEGER MILLI-UNITS on the wire (1.5 kg = 1500). */
export type QtyMilli = number;

export const UNIT_CODES = ['KG', 'G', 'L', 'ML', 'PCS', 'BOX', 'BOTTLE', 'CAN', 'PACK', 'PORTION'] as const;
export type UnitCode = (typeof UNIT_CODES)[number];

export interface InvWarehouse {
  id: string;
  branchId: string | null;
  code: string;
  name: string;
  warehouseType: string;
  stationId: string | null;
  managerUserId: string | null;
  address: string | null;
  allowNegative: boolean;
  isActive: boolean;
  rowVersion: number;
}

export interface InvWarehousesResponse {
  warehouses: InvWarehouse[];
}

export interface InvSupplier {
  id: string;
  code: string;
  name: string;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  taxCode: string | null;
  paymentTermsDays: number;
  leadTimeDays: number;
  rating: number | null;
  note: string | null;
  isActive: boolean;
  rowVersion: number;
  createdAt: Millis;
}

export interface InvSuppliersResponse {
  suppliers: InvSupplier[];
}

export interface InvIngredient {
  id: string;
  code: string;
  nameVi: string;
  category: string;
  baseUnitCode: UnitCode;
  stockUnitCode: UnitCode;
  purchaseUnitCode: UnitCode;
  isSemiFinished: boolean;
  shelfLifeDays: number | null;
  storageType: string;
  trackBatch: boolean;
  trackExpiry: boolean;
  defaultCostMinor: Minor;
  currency: string;
  allowedWastageBp: number;
  minStockMilli: QtyMilli;
  maxStockMilli: QtyMilli | null;
  reorderPointMilli: QtyMilli;
  reorderQtyMilli: QtyMilli;
  preferredSupplierId: string | null;
  barcode: string | null;
  isActive: boolean;
  rowVersion: number;
}

export interface InvIngredientsResponse {
  ingredients: InvIngredient[];
  total: number;
  limit: number;
  offset: number;
}

export interface StockLine {
  warehouseId: string;
  branchId: string | null;
  ingredientId: string;
  ingredientCode: string;
  ingredientName: string;
  category: string;
  quantityMilli: QtyMilli;
  reservedMilli: QtyMilli;
  availableMilli: QtyMilli;
  unitCode: UnitCode;
  avgCostMinor: Minor;
  lastCostMinor: Minor;
  totalValueMinor: Minor;
  trackBatch: boolean;
  reorderPointMilli: QtyMilli;
  minStockMilli: QtyMilli;
  reorderNeeded: boolean;
  belowMin: boolean;
  lastMovementAt: Millis | null;
  lastCountAt: Millis | null;
}

export interface StockResponse {
  stock: StockLine[];
}

export interface InvPurchaseOrder {
  id: string;
  branchId: string | null;
  warehouseId: string;
  supplierId: string;
  poNo: string;
  orderDate: string;
  expectedDate: string | null;
  status: string;
  subtotalMinor: Minor;
  taxMinor: Minor;
  totalMinor: Minor;
  note: string | null;
  requestedBy: string | null;
  approvedBy: string | null;
  approvedAt: Millis | null;
  rowVersion: number;
  createdAt: Millis;
}

export interface InvPoItem {
  id: string;
  ingredientId: string;
  quantityMilli: QtyMilli;
  unitCode: UnitCode;
  unitPriceMinor: Minor;
  taxBp: number;
  lineTotalMinor: Minor;
  receivedQtyMilli: QtyMilli;
  remainingQtyMilli: QtyMilli;
  note: string | null;
  sortOrder: number;
}

export interface InvPoListResponse {
  purchaseOrders: InvPurchaseOrder[];
}

export interface InvPoDetailResponse {
  purchaseOrder: InvPurchaseOrder;
  items: InvPoItem[];
}

export interface ReceiptItemInput {
  ingredientId: string;
  purchaseOrderItemId?: string | null;
  receivedQtyMilli: QtyMilli;
  acceptedQtyMilli?: QtyMilli;
  rejectedQtyMilli?: QtyMilli;
  rejectReason?: 'DAMAGED' | 'EXPIRED' | 'WRONG_ITEM' | 'QUALITY' | 'SHORT' | 'OTHER' | null;
  unitCode: UnitCode;
  unitPriceMinor: Minor;
  batchNo?: string | null;
  expiryDate?: string | null;
}

export interface ReceiptBody {
  branchId: string;
  warehouseId: string;
  supplierId: string;
  purchaseOrderId?: string | null;
  deliveryNoteNo?: string | null;
  invoiceNo?: string | null;
  note?: string | null;
  items: ReceiptItemInput[];
}

export interface ReceiptResponse {
  receipt: {
    receiptId: string;
    receiptNo: string;
    totalMinor: Minor;
    movementCount: number;
    lines: unknown[];
  };
}

export interface IssueBody {
  branchId: string;
  warehouseId: string;
  purpose?: 'MANUAL' | 'RECIPE_PREP';
  referenceId?: string | null;
  note?: string | null;
  items: Array<{ ingredientId: string; quantityMilli: QtyMilli }>;
}

export interface IssueResponse {
  issue: { totalCostMinor: Minor; lines: unknown[] };
}

export const WASTAGE_TYPES = [
  'PREP_LOSS',
  'COOKING_ERROR',
  'EXPIRED',
  'DAMAGED',
  'SPILLED',
  'CUSTOMER_RETURN',
  'REMAKE',
  'QUALITY_CHECK',
  'OVER_PRODUCTION',
  'OTHER',
] as const;

export type WastageType = (typeof WASTAGE_TYPES)[number];

export interface WastageBody {
  branchId: string;
  warehouseId: string;
  stationId?: string | null;
  ingredientId: string;
  quantityMilli: QtyMilli;
  wastageType: WastageType;
  businessDate?: string;
  responsibleEmployeeId?: string | null;
  evidenceFileId?: string | null;
}

export interface WastageResponse {
  wastage: { wastageId: string; costMinor: Minor };
}

export interface StockCount {
  id: string;
  branchId: string;
  warehouseId: string;
  countNo: string;
  countType: string;
  businessDate: string;
  status: string;
  totalVarianceValueMinor: Minor;
  itemCount: number;
  varianceItemCount: number;
  rowVersion: number;
}

export interface StockCountItem {
  id: string;
  ingredientId: string;
  systemQtyMilli: QtyMilli;
  countedQtyMilli: QtyMilli | null;
  varianceMilli: QtyMilli;
  varianceValueMinor: Minor;
  unitCode: UnitCode;
  unitCostMinor: Minor;
  varianceReason: string | null;
  note: string | null;
}

export interface StockCountsResponse {
  counts: StockCount[];
}

export interface StockCountDetailResponse {
  count: StockCount;
  items: StockCountItem[];
}

export interface StockCountStartResponse {
  count: StockCount;
  itemCount: number;
}

export interface CountItemsInput {
  items: Array<{
    ingredientId: string;
    countedQtyMilli: QtyMilli;
    recountQtyMilli?: QtyMilli | null;
    varianceReason?:
      | 'THEFT'
      | 'WASTAGE'
      | 'MISCOUNT'
      | 'UNRECORDED_USE'
      | 'SUPPLIER_SHORT'
      | 'SYSTEM_ERROR'
      | 'EXPIRY'
      | 'UNKNOWN'
      | null;
    note?: string | null;
  }>;
}
